/**
 * API Client for DJ Bus Workers
 *
 * Connects to Cloudflare Workers API
 */

import axios from 'axios';

// API Base URL - dev server 또는 배포된 Workers URL
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://127.0.0.1:8787';

// Axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor (로깅용)
api.interceptors.request.use(
  (config) => {
    console.log(`[API] ${config.method?.toUpperCase()} ${config.url}`, config.data);
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor (에러 처리)
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.error('[API Error]', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// 전역 401 처리 (admin API 등 axios 직접 호출 포함)
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const store = JSON.parse(localStorage.getItem('auth-storage') || '{}');
      if (store?.state?.isAuthenticated) {
        localStorage.removeItem('auth-storage');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// ============================================
// Authentication API
// ============================================

export const authAPI = {
  /**
   * 로그인 - Supabase Edge Function 사용
   * @param {Object} data - { id, pass, autoLogin }
   */
  login: async (data) => {
    // Supabase Edge Function으로 직접 요청
    const response = await axios.post(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/api/auth`,
      data,
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
        },
      }
    );
    return response.data;
  },
};

// ============================================
// Reservation API - Supabase Edge Functions 사용
// ============================================

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

const supabaseHeaders = {
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
  'apikey': SUPABASE_ANON_KEY,
};

export const reservationAPI = {
  /**
   * 즉시 예약 실행
   * @param {Object} data - { userId, busSeq, seatNo, stopSeq }
   */
  execute: async (data) => {
    const response = await axios.post(
      `${SUPABASE_URL}/functions/v1/api/reservation/execute`,
      data,
      { headers: supabaseHeaders }
    );
    return response.data;
  },

  /**
   * 노선 조회
   * @param {string} userId
   * @param {string} direction - 'UP' or 'DOWN'
   */
  getRoutes: async (userId, direction) => {
    const response = await axios.get(
      `${SUPABASE_URL}/functions/v1/api/reservation/routes?userId=${userId}&direction=${direction}`,
      { headers: supabaseHeaders }
    );
    return response.data;
  },

  /**
   * 시간표 조회
   * @param {string} userId
   * @param {string} direction
   * @param {string} lineGroupSeq
   */
  getTimetable: async (userId, direction, lineGroupSeq) => {
    const response = await axios.get(
      `${SUPABASE_URL}/functions/v1/api/reservation/timetable?userId=${userId}&direction=${direction}&lineGroupSeq=${lineGroupSeq}`,
      { headers: supabaseHeaders }
    );
    return response.data;
  },

  /**
   * 좌석 정보 조회
   * @param {string} userId
   * @param {string} busSeq
   */
  getSeats: async (userId, busSeq) => {
    const response = await axios.get(
      `${SUPABASE_URL}/functions/v1/api/reservation/seats?userId=${userId}&busSeq=${busSeq}`,
      { headers: supabaseHeaders }
    );
    return response.data;
  },

  /**
   * 예약 내역 조회
   * @param {string} userId
   */
  getReservations: async (userId) => {
    const response = await axios.get(
      `${SUPABASE_URL}/functions/v1/api/reservation/list?userId=${userId}`,
      { headers: supabaseHeaders }
    );
    return response.data;
  },

  /**
   * 예약 취소
   * @param {Object} data - { userId, busSeq, seatNo }
   */
  cancel: async (data) => {
    const response = await axios.post(
      `${SUPABASE_URL}/functions/v1/api/reservation/cancel`,
      data,
      { headers: supabaseHeaders }
    );
    return response.data;
  },
};

// ============================================
// Schedule API - Supabase Edge Functions 사용
// ============================================

export const scheduleAPI = {
  /**
   * 스케줄 목록 조회
   * @param {string} userId
   */
  list: async (userId) => {
    const response = await axios.get(
      `${SUPABASE_URL}/functions/v1/api/schedules/list?userId=${userId}`,
      { headers: supabaseHeaders }
    );
    return response.data;
  },

  /**
   * 스케줄 생성
   * @param {Object} data - Schedule data
   */
  create: async (data) => {
    const response = await axios.post(
      `${SUPABASE_URL}/functions/v1/api/schedules/create`,
      data,
      { headers: supabaseHeaders }
    );
    return response.data;
  },

  /**
   * 스케줄 수정
   * @param {string} scheduleId
   * @param {Object} data - Updated fields
   */
  update: async (scheduleId, data) => {
    const response = await axios.put(
      `${SUPABASE_URL}/functions/v1/api/schedules/${scheduleId}`,
      data,
      { headers: supabaseHeaders }
    );
    return response.data;
  },

  /**
   * 일괄 스케줄 생성
   * @param {Array} schedules - Array of schedule data
   */
  createBulk: async (schedules) => {
    const response = await axios.post(
      `${SUPABASE_URL}/functions/v1/api/schedules/create-bulk`,
      { schedules },
      { headers: supabaseHeaders }
    );
    return response.data;
  },

  /**
   * 좌석 선점 상태 확인
   * @param {Object} data - { dayOfWeek, direction, busTime, lineGroupSeq, seatPreferences, excludeScheduleId? }
   * @returns {{ success, available, totalAllocated }}
   */
  checkSeats: async (data) => {
    const response = await axios.post(
      `${SUPABASE_URL}/functions/v1/api/schedules/check-seats`,
      data,
      { headers: supabaseHeaders }
    );
    return response.data;
  },

  /**
   * 스케줄 삭제
   * @param {string} scheduleId
   */
  delete: async (scheduleId) => {
    const response = await axios.delete(
      `${SUPABASE_URL}/functions/v1/api/schedules/${scheduleId}`,
      { headers: supabaseHeaders }
    );
    return response.data;
  },
};

// ============================================
// Snapshot API - 노선 스냅샷
// ============================================

export const snapshotAPI = {
  /**
   * 최신 스냅샷 조회
   * @param {Object|string} [options] - 문자열이면 capturedDay, 객체면 {capturedDay, excludeDay}
   */
  getLatest: async (options) => {
    const params = new URLSearchParams();
    if (typeof options === 'string') {
      params.set('capturedDay', options);
    } else if (options) {
      if (options.capturedDay) params.set('capturedDay', options.capturedDay);
      if (options.excludeDay) params.set('excludeDay', options.excludeDay);
    }
    const qs = params.toString() ? `?${params.toString()}` : '';
    const response = await axios.get(
      `${SUPABASE_URL}/functions/v1/snapshot/latest${qs}`,
      { headers: supabaseHeaders }
    );
    return response.data;
  },
};

// ============================================
// Logs API - Supabase Client 직접 사용
// ============================================

export const logsAPI = {
  /**
   * 예약 로그 조회
   * @param {string} userId
   * @param {Object} options - { limit, offset }
   */
  get: async (userId, options = {}) => {
    const { limit = 50, offset = 0 } = options;
    const response = await axios.get(
      `${SUPABASE_URL}/functions/v1/api/logs/list?userId=${userId}&limit=${limit}&offset=${offset}`,
      { headers: supabaseHeaders }
    );
    return response.data;
  },
};

// ============================================
// Preferences API - 사용자 환경설정
// ============================================

export const preferencesAPI = {
  /**
   * 사용자 환경설정 조회
   * @param {string} userId
   */
  get: async (userId) => {
    const response = await axios.get(
      `${SUPABASE_URL}/functions/v1/api/preferences?userId=${userId}`,
      { headers: supabaseHeaders }
    );
    return response.data;
  },

  /**
   * 사용자 환경설정 업데이트
   * @param {Object} data - { userId, lastSeatPreferences, lastStopSeq, lastStopName, lastDirection, lastRouteName }
   */
  update: async (data) => {
    const response = await axios.post(
      `${SUPABASE_URL}/functions/v1/api/preferences`,
      data,
      { headers: supabaseHeaders }
    );
    return response.data;
  },
};

// ============================================
// User API - 사용자 정보 조회
// ============================================

// ============================================
// Point Charge API - 포인트 충전
// ============================================

export const pointAPI = {
  getChargeList: async (userId) => {
    const response = await axios.get(
      `${SUPABASE_URL}/functions/v1/api/reservation/charge/list?userId=${userId}`,
      { headers: supabaseHeaders }
    );
    return response.data;
  },

  requestCharge: async (data) => {
    const response = await axios.post(
      `${SUPABASE_URL}/functions/v1/api/reservation/charge/request`,
      data,
      { headers: supabaseHeaders }
    );
    return response.data;
  },

  cancelCharge: async (data) => {
    const response = await axios.post(
      `${SUPABASE_URL}/functions/v1/api/reservation/charge/cancel`,
      data,
      { headers: supabaseHeaders }
    );
    return response.data;
  },
};

export const userAPI = {
  getInfo: async (userId) => {
    const response = await axios.get(
      `${SUPABASE_URL}/functions/v1/api/reservation/user-info?userId=${userId}`,
      { headers: supabaseHeaders }
    );
    return response.data;
  },
};

// ============================================
// Admin API - JWT 인증 기반 관리자 API
// ============================================

export const adminAPI = {
  /**
   * JWT 토큰으로 인증하는 헤더 생성
   */
  _getHeaders: () => {
    // 순환 import 방지: 런타임에 store에서 직접 접근
    // eslint-disable-next-line no-undef
    const store = typeof window !== 'undefined'
      ? JSON.parse(localStorage.getItem('auth-storage') || '{}')
      : {};
    const jwtToken = store?.state?.user?.jwtToken;
    return {
      ...supabaseHeaders,
      'Authorization': `Bearer ${jwtToken}`,
    };
  },

  /**
   * 사용자 목록 조회
   * @param {Object} params - { page, limit, search }
   */
  getUsers: async (params = {}) => {
    const { page = 1, limit = 20, search = '' } = params;
    const offset = (page - 1) * limit;
    const query = new URLSearchParams({ limit, offset, ...(search ? { search } : {}) }).toString();
    const response = await axios.get(
      `${SUPABASE_URL}/functions/v1/api/admin/users?${query}`,
      { headers: adminAPI._getHeaders() }
    );
    return response.data;
  },

  /**
   * 사용자 정보 업데이트
   * @param {string} id - 사용자 ID
   * @param {Object} data - { role, is_active }
   */
  updateUser: async (id, data) => {
    const response = await axios.patch(
      `${SUPABASE_URL}/functions/v1/api/admin/users/${id}`,
      data,
      { headers: adminAPI._getHeaders() }
    );
    return response.data;
  },

  /**
   * 예약 로그 목록 조회
   * @param {Object} params - { page, limit, status, search }
   */
  getLogs: async (params = {}) => {
    const { page = 1, limit = 20, status = '', search = '' } = params;
    const offset = (page - 1) * limit;
    const query = new URLSearchParams({
      limit,
      offset,
      ...(status ? { status } : {}),
      ...(search ? { userId: search } : {}),
    }).toString();
    const response = await axios.get(
      `${SUPABASE_URL}/functions/v1/api/admin/logs?${query}`,
      { headers: adminAPI._getHeaders() }
    );
    return response.data;
  },

  /**
   * 스케줄 목록 조회
   * @param {Object} params - { day }
   */
  getSchedules: async (params = {}) => {
    const { day = '' } = params;
    const query = new URLSearchParams({ ...(day ? { day } : {}) }).toString();
    const response = await axios.get(
      `${SUPABASE_URL}/functions/v1/api/admin/schedules${query ? `?${query}` : ''}`,
      { headers: adminAPI._getHeaders() }
    );
    return response.data;
  },

  /**
   * 관리자 통계 조회
   */
  getStats: async () => {
    const response = await axios.get(
      `${SUPABASE_URL}/functions/v1/api/admin/stats`,
      { headers: adminAPI._getHeaders() }
    );
    return response.data;
  },

  /**
   * 푸시 토큰 등록 사용자 목록
   */
  getPushTokens: async () => {
    const response = await axios.get(
      `${SUPABASE_URL}/functions/v1/api/admin/push-tokens`,
      { headers: adminAPI._getHeaders() }
    );
    return response.data;
  },

  /**
   * 커스텀 알림 발송
   * @param {Object} data - { title, message, targetUserIds? }
   */
  sendNotification: async (data) => {
    const response = await axios.post(
      `${SUPABASE_URL}/functions/v1/api/admin/notifications/send`,
      data,
      { headers: adminAPI._getHeaders() }
    );
    return response.data;
  },

  /**
   * 런타임 설정 목록 조회 (app_settings)
   */
  getSettings: async () => {
    const response = await axios.get(
      `${SUPABASE_URL}/functions/v1/api/admin/settings`,
      { headers: adminAPI._getHeaders() }
    );
    return response.data;
  },

  /**
   * 런타임 설정 업데이트 (upsert)
   * @param {string} key
   * @param {*} value - JSON 직렬화 가능한 값
   * @param {string} [description]
   */
  updateSetting: async (key, value, description) => {
    const response = await axios.put(
      `${SUPABASE_URL}/functions/v1/api/admin/settings/${encodeURIComponent(key)}`,
      { value, description },
      { headers: adminAPI._getHeaders() }
    );
    return response.data;
  },
};

// ============================================
// Notification API - 사용자 알림 내역
// ============================================

export const notificationAPI = {
  list: async (userId, options = {}) => {
    const { limit = 20, offset = 0 } = options;
    const response = await axios.get(
      `${SUPABASE_URL}/functions/v1/api/notifications/list?userId=${userId}&limit=${limit}&offset=${offset}`,
      { headers: supabaseHeaders }
    );
    return response.data;
  },

  unreadCount: async (userId) => {
    const response = await axios.get(
      `${SUPABASE_URL}/functions/v1/api/notifications/unread-count?userId=${userId}`,
      { headers: supabaseHeaders }
    );
    return response.data;
  },

  markRead: async (userId, notificationId) => {
    const response = await axios.patch(
      `${SUPABASE_URL}/functions/v1/api/notifications/read`,
      { userId, notificationId: notificationId || null },
      { headers: supabaseHeaders }
    );
    return response.data;
  },
};

// Push Token API
export const pushTokenAPI = {
  register: async (userId, token) => {
    const response = await axios.post(
      `${SUPABASE_URL}/functions/v1/api/push-tokens/register`,
      { userId, token },
      { headers: supabaseHeaders }
    );
    return response.data;
  },

  unregister: async (userId, token) => {
    const response = await axios.post(
      `${SUPABASE_URL}/functions/v1/api/push-tokens/unregister`,
      { userId, token },
      { headers: supabaseHeaders }
    );
    return response.data;
  },

  exists: async (userId, token) => {
    const response = await axios.get(
      `${SUPABASE_URL}/functions/v1/api/push-tokens/exists?userId=${encodeURIComponent(userId)}&token=${encodeURIComponent(token)}`,
      { headers: supabaseHeaders }
    );
    return response.data.exists;
  },
};

// ============================================
// Referral API - 리퍼럴 시스템
// ============================================

export const referralAPI = {
  getMyCode: async (userId) => {
    const response = await axios.get(
      `${SUPABASE_URL}/functions/v1/api/referrals/my-code?userId=${userId}`,
      { headers: supabaseHeaders }
    );
    return response.data;
  },

  apply: async (userId, code) => {
    const response = await axios.post(
      `${SUPABASE_URL}/functions/v1/api/referrals/apply`,
      { userId, code },
      { headers: supabaseHeaders }
    );
    return response.data;
  },

  getDashboard: async (userId) => {
    const response = await axios.get(
      `${SUPABASE_URL}/functions/v1/api/referrals/dashboard?userId=${userId}`,
      { headers: supabaseHeaders }
    );
    return response.data;
  },

  getStatus: async (userId) => {
    const response = await axios.get(
      `${SUPABASE_URL}/functions/v1/api/referrals/status?userId=${userId}`,
      { headers: supabaseHeaders }
    );
    return response.data;
  },
};

export default api;
